import React from "react";
import CardItem from "./CardItem";
import BoxShadow from "./BoxShadow";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * Sahadaki dokunmatik ekranların üst bilgi kartı (Asma / Asma SPV / Toplama).
 *
 * Eski düzen sabit piksel genişlikleriyle (`titleWidth="250px"`, `pl-32`)
 * hizalanıyordu; uzun sipariş adı kartın dışına taşıyor, "Son Güncellenme"
 * satırı verilerin üstüne biniyordu. Artık:
 *   - Sipariş adı tam genişlikte tek satır, sığmazsa üç noktayla kısalır.
 *   - Sayısal alanlar eşit sütunlu ızgarada, etiket üstte / değer altta.
 *   - "Kalan" vurgulu: operatörün en sık baktığı sayı.
 *   - Son güncelleme kartın alt şeridinde, veri alanından ayrı.
 */

/** Askının artıp artmadığı — üç durumlu, renk kodlu. */
function useHangerState(lastHanger, t) {
  if (lastHanger?.increased === true) {
    return { text: t("datacard.increased"), tone: "text-state-ok" };
  }
  if (lastHanger?.increased === false) {
    return { text: t("datacard.notIncreased"), tone: "text-state-bad" };
  }
  return { text: t("datacard.waiting"), tone: "text-content-muted" };
}

const Footer = ({ children }) => (
  <div className="mt-auto flex justify-end border-t border-line pt-1 text-[12px] font-semibold text-content-muted">
    {children}
  </div>
);

export default function DataCard({
  cardData = {},
  batchRefreshTime,
  lastHanger,
  alarm,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const hanger = useHangerState(lastHanger, t);

  if (location.pathname === "/toplama") {
    const remaining = cardData?.target - cardData?.fiiliMiktarGovde;
    return (
      <BoxShadow>
        <div className="flex h-full flex-col gap-1">
          <CardItem data={cardData?.description} title={t("datacard.order")} />

          <div className="grid flex-1 grid-cols-3 gap-x-4 laptop:grid-cols-5">
            <CardItem
              data={lastHanger?.hanger_no || t("datacard.waiting")}
              title={t("datacard.hangerNo")}
            />
            <CardItem
              data={cardData?.fiiliMiktarGovde}
              title={t("datacard.actual")}
            />
            <CardItem data={cardData?.target} title={t("datacard.planned")} />
            <div className="flex min-w-0 flex-col justify-center gap-0.5">
              <span className="truncate text-[12px] font-semibold uppercase tracking-label text-content-muted">
                {t("datacard.process")}
              </span>
              <span className={`truncate text-[20px] font-bold ${hanger.tone}`}>
                {hanger.text}
              </span>
            </div>
            <CardItem
              data={Number.isFinite(remaining) ? remaining : ""}
              title={t("datacard.remaining")}
              emphasis
            />
          </div>

          <Footer>
            {lastHanger?.event ? `${t("datacard.status")}: ${lastHanger.event}` : ""}
          </Footer>
        </div>
      </BoxShadow>
    );
  }

  const planned = cardData?.fields?.uretim;
  const actual = cardData?.fields?.actual_production_asma;
  const remaining = planned - actual;

  return (
    <BoxShadow>
      <div className="flex h-full flex-col gap-1">
        {/* Sipariş — tam genişlik, taşarsa kısalır */}
        <CardItem data={cardData?.fields?.siparis} title={t("datacard.order")} />

        {/* Sayısal alanlar — eşit sütunlar */}
        <div className="grid flex-1 grid-cols-3 gap-x-4 laptop:grid-cols-5">
          <CardItem
            data={lastHanger?.hanger_no || t("datacard.waiting")}
            title={t("datacard.hangerNo")}
          />
          <CardItem data={actual} title={t("datacard.actual")} />
          <CardItem data={planned} title={t("datacard.planned")} />
          <div className="flex min-w-0 flex-col justify-center gap-0.5">
            <span className="truncate text-[12px] font-semibold uppercase tracking-label text-content-muted">
              {t("datacard.process")}
            </span>
            <span className={`truncate text-[20px] font-bold ${hanger.tone}`}>
              {hanger.text}
            </span>
          </div>
          <CardItem
            data={Number.isFinite(remaining) ? remaining : ""}
            title={t("datacard.remaining")}
            emphasis
          />
        </div>

        {/* Alt şerit: durum, artmamış askı, son güncelleme */}
        <Footer>
          <div className="flex w-full flex-wrap items-center justify-between gap-x-4">
            <span className="truncate">
              {t("datacard.status")}:{" "}
              <span className="text-content">
                {lastHanger?.event || t("datacard.waiting")}
              </span>
              <span className="ml-4">
                {t("datacard.notIncreasedHangerCount")}:{" "}
                <span className="text-content">
                  {alarm?.passed_hanger_num ?? "—"}
                </span>
              </span>
            </span>
            <span className="shrink-0">
              {t("datacard.lastUpdate")} {batchRefreshTime?.split(" ")[0]}{" "}
              {batchRefreshTime?.slice(10, 16) ||
                alarm?.last_refreshed_plan?.slice(0, 16)}
            </span>
          </div>
        </Footer>
      </div>
    </BoxShadow>
  );
}
